/**
 * 字典管理服务
 */

import { httpClient } from './httpClient';
import { API_ENDPOINTS } from './api/config';

// 字典类型服务
export const dictTypeService = {
  async getList(params: any) {
    return httpClient.get(API_ENDPOINTS.DICT.TYPE_LIST, { params });
  },
  
  async getById(id: number) {
    return httpClient.get(`${API_ENDPOINTS.DICT.TYPE_DETAIL}/${id}`);
  },
  
  async create(data: any) {
    return httpClient.post(API_ENDPOINTS.DICT.TYPE_CREATE, data);
  },
  
  async update(data: any) {
    return httpClient.put(API_ENDPOINTS.DICT.TYPE_UPDATE, data);
  },
  
  async delete(id: number) {
    return httpClient.delete(`${API_ENDPOINTS.DICT.TYPE_DELETE}/${id}`);
  },
  
  async batchDelete(ids: number[]) {
    return httpClient.delete(API_ENDPOINTS.DICT.TYPE_BATCH_DELETE, { data: ids });
  }
};

// 字典数据服务
export const dictDataService = {
  async getList(params: any) {
    return httpClient.get(API_ENDPOINTS.DICT.DATA_LIST, { params });
  },
  
  async getById(id: number) {
    return httpClient.get(`${API_ENDPOINTS.DICT.DATA_DETAIL}/${id}`);
  },
  
  async create(data: any) {
    return httpClient.post(API_ENDPOINTS.DICT.DATA_CREATE, data);
  },
  
  async update(data: any) {
    return httpClient.put(API_ENDPOINTS.DICT.DATA_UPDATE, data);
  },
  
  async delete(id: number) {
    return httpClient.delete(`${API_ENDPOINTS.DICT.DATA_DELETE}/${id}`);
  },
  
  async batchDelete(ids: number[]) {
    return httpClient.delete(API_ENDPOINTS.DICT.DATA_BATCH_DELETE, { data: ids });
  },

  // 新增：获取完整字典数据的方法
  async getAll(params?: { dictType?: string; status?: string }) {
    return httpClient.get(API_ENDPOINTS.DICT.DATA_ALL, { params });
  },

  async getAllEnabled() {
    return httpClient.get(API_ENDPOINTS.DICT.DATA_ALL_ENABLED);
  },

  async getAllEnabledDictData() {
    return httpClient.get(API_ENDPOINTS.DICT.DATA_ALL_ENABLED);
  },

  async getAllByType(dictType: string) {
    // 使用查询参数方式，路径更简洁：/api/sys/dict/data/all?dictType=xxx
    return httpClient.get(API_ENDPOINTS.DICT.DATA_ALL, { params: { dictType, status: '1' } });
  },

  // 新增：批量获取多个字典类型的数据
  async getBatchByTypes(dictTypes: string[]) {
    return httpClient.post(API_ENDPOINTS.DICT.DATA_BATCH, dictTypes);
  }
};

// 根据字典类型获取字典数据（优化版本：使用不分页接口）
export const getDictByType = async (dictType: string) => {
  try {
    // 使用新的不分页接口获取完整数据
    const response = await dictDataService.getAllByType(dictType);
    
    if (response.code === 200) {
      // 直接返回数据列表，不需要处理分页结构
      return response.data || [];
    } else {
      throw new Error(response.message || '获取字典数据失败');
    }
  } catch (error) {
    console.error('获取字典数据失败:', error);
    throw error;
  }
};

// 根据字典类型获取字典数据（分页版本，保留兼容性）
export const getDictByTypeWithPagination = async (dictType: string) => {
  try {
    const response = await dictDataService.getList({
      dictType,
      status: 1, // 只获取正常状态的字典
      current: 1,
      size: 1000 // 获取所有数据
    });
    
    if (response.code === 200) {
      return response.data.records || [];
    } else {
      throw new Error(response.message || '获取字典数据失败');
    }
  } catch (error) {
    console.error('获取字典数据失败:', error);
    throw error;
  }
};

// 批量获取多个字典类型的数据
export const getDictsByTypes = async (dictTypes: string[]) => {
  try {
    const promises = dictTypes.map(async (dictType) => {
      const data = await getDictByType(dictType);
      return { dictType, data };
    });

    const results = await Promise.all(promises);
    const dictsMap: Record<string, any[]> = {};
    
    results.forEach(({ dictType, data }) => {
      dictsMap[dictType] = data;
    });
    
    return dictsMap;
  } catch (error) {
    console.error('批量获取字典数据失败:', error);
    throw error;
  }
};

// 统一的字典服务对象
export const dictService = {
  ...dictDataService,
  ...dictTypeService,
  getDictByType,
  getDictsByTypes,
};

export default { dictTypeService, dictDataService, getDictByType, getDictsByTypes, dictService };